import { useEffect, useRef } from 'react';
import { Minus, Plus, ShoppingBag, X } from 'lucide-react';
import { useLocalNavigate as useNavigate } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/catalog';
import { ProductImage } from '@/lib/images';

export const Cart = ({ isOpen, onClose, cart, onUpdateQuantity, onRemoveItem }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const closeRef = useRef(null);

  const lineKey = (item) => `${item.product_id}::${item.selected_variant || ''}`;
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  const qtyBtn =
    'grid h-11 w-11 place-items-center rounded-full border-2 border-candy-ink/15 bg-candy-surface transition-colors hover:border-candy-ink';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-candy-ink/40 backdrop-blur-sm animate-in fade-in-0 duration-200" onClick={onClose} data-testid="cart-overlay" />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-title"
        data-testid="cart-sidebar"
        className="fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-candy-bg animate-in slide-in-from-right duration-200 ease-out md:w-[420px] md:rounded-l-block"
      >
        <div className="flex items-center justify-between px-6 py-5">
          <h2 id="cart-title" className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-[-0.02em]">
            <ShoppingBag className="h-6 w-6" aria-hidden="true" />
            {t('cart.title')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            data-testid="cart-close-btn"
            onClick={onClose}
            aria-label={t('cart.close')}
            className="grid h-11 w-11 place-items-center rounded-full bg-candy-surface"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {cart.length === 0 ? (
            <div className="py-12 text-center" data-testid="empty-cart-message">
              <p className="text-candy-ink-muted">{t('cart.empty')}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {cart.map((item) => {
                const key = lineKey(item);
                return (
                  <li key={key} data-testid={`cart-item-${key}`} className="flex gap-4 rounded-card bg-candy-surface p-3.5">
                    <ProductImage
                      src={item.image_url}
                      alt={item.name}
                      widths={[200]}
                      sizes="80px"
                      width="80"
                      height="80"
                      className="h-20 w-20 flex-none rounded-2xl bg-candy-bg object-contain p-1"
                    />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <h3 className="font-heading font-bold leading-tight">{item.name}</h3>
                      {item.selected_variant && (
                        <p className="text-xs text-candy-ink-muted">
                          {t('cart.size')}: <span className="font-medium text-candy-ink">{item.selected_variant}</span>
                        </p>
                      )}
                      <p className="font-heading font-extrabold">{formatPrice(item.price)}</p>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          data-testid={`decrease-qty-${key}`}
                          onClick={() => onUpdateQuantity(key, item.quantity - 1)}
                          aria-label={t('cart.decrease')}
                          className={qtyBtn}
                        >
                          <Minus className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <span className="w-7 text-center font-bold tabular-nums" data-testid={`qty-${key}`}>
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          data-testid={`increase-qty-${key}`}
                          onClick={() => onUpdateQuantity(key, item.quantity + 1)}
                          aria-label={t('cart.increase')}
                          className={qtyBtn}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          data-testid={`remove-item-${key}`}
                          onClick={() => onRemoveItem(key)}
                          className="ml-auto min-h-[44px] px-2 text-xs font-bold text-candy-ink-muted underline-offset-2 hover:text-destructive hover:underline"
                        >
                          {t('cart.remove')}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="space-y-3 border-t border-candy-line px-6 py-5">
          {cart.length > 0 && (
            <>
              <div className="flex items-center justify-between text-lg">
                <span className="font-bold">{t('cart.total')}</span>
                <span className="font-heading text-2xl font-extrabold" data-testid="cart-total">
                  {formatPrice(total)}
                </span>
              </div>
              <button
                type="button"
                data-testid="checkout-btn"
                onClick={handleCheckout}
                className="h-[52px] w-full rounded-full bg-candy-ink text-lg font-bold text-white transition-colors hover:bg-candy-ink/90"
              >
                {t('cart.checkout')}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="continue-shopping-btn"
            className="h-[52px] w-full rounded-full border-2 border-candy-ink bg-candy-surface text-base font-bold transition-colors hover:bg-candy-pink"
          >
            {t('cart.continue')}
          </button>
        </div>
      </aside>
    </>
  );
};
